import React, { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { t } from '@lingui/macro'
import styled from 'styled-components'
import Tab from 'components/tab/Tab'
import Tabs from 'components/tab/Tabs'
import DashboardHistoryTab from './tabs/history/DashboardHistoryTab'
import ToggleDrawer from 'components/Header/ToggleDrawer'
import AppBar from 'components/AppBar'
import AppBody from 'pages/AppBody'
import CurrencyDropdown from 'components/Dropdowns/CurrencyDropdown'
import TabPanel from 'components/tab/TabPanel'
import { AutoColumn } from 'components/Column'
import { ResponsiveRow } from 'components/Row'
import Pools from './components/Pools'
import { BodyScroller, CurrencySelectWrapper } from 'theme'
// HOOKS
import { useActiveWeb3React } from 'hooks/web3'
import { useAllTokenBalances } from 'state/wallet/hooks'
import { formatCurrencyAmount } from 'utils/formatCurrencyAmount'
import { useApiTokens } from 'hooks/useApi'
import { AssetsTableData } from 'components/Table/types'
import { LoaderWrapped } from 'theme/components'
import { WalletConnect } from 'components/WalletConnect'

const StyledAppBar = styled(AppBar)`
  padding: 0px 2rem;
  ${({ theme }) => theme.mediaWidth.upToSmall`
  padding: 0px 0.625rem;
  `}
`

type WalletProps =
  | {
      balanceUSD: number
      data: AssetsTableData[]
      loader?: JSX.Element | boolean
    }
  | undefined

export function useWalletData(): WalletProps {
  const { data, abortController } = useApiTokens()
  const balances = useAllTokenBalances()

  useEffect(() => {
    return () => {
      abortController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return useMemo((): WalletProps => {
    if (!balances || !data || !data.length) return { balanceUSD: 0, data: [], loader: <LoaderWrapped /> }

    const balancesFormated: AssetsTableData[] = []
    let totalWalletBalance = 0

    Object.keys(balances).map(async (address) => {
      const symbol = balances[address]?.currency.symbol || ''
      const balance = parseFloat(formatCurrencyAmount(balances[address], 8))
      const price = parseFloat(data.find((x) => x.address === address)?.priceUSD || '')
      const total = balance * price
      totalWalletBalance += total
      balancesFormated.push({
        address,
        symbol,
        balance,
        price,
        total,
      })
    })

    balancesFormated.sort((a, b) => (a.symbol > b.symbol ? 1 : -1))

    return { balanceUSD: totalWalletBalance, data: balancesFormated }
  }, [balances, data])
}

export default function Account() {
  const [activeTab, setActiveTab] = useState<number>(0)
  const { state } = useLocation<any>()
  const { account } = useActiveWeb3React()

  useEffect(() => {
    const url = new URL(window.location.href.replace('/#', ''))
    const tab = url.searchParams.get('tab')
    if (!tab) return

    setActiveTab(parseInt(tab))
  }, [])

  useEffect(() => {
    if (!state?.type) {
      return
    }

    const scrollTo = setTimeout(
      () => document.querySelector('#top-tokens-table')?.scrollIntoView({ behavior: 'smooth' }),
      800
    )
    return () => {
      clearTimeout(scrollTo)
    }
  }, [state])

  const TabChangeHandler: any = (e: any, newValue: any) => setActiveTab(newValue)

  return (
    <>
      <StyledAppBar>
        <ToggleDrawer />
        <Tabs value={activeTab} onChange={TabChangeHandler}>
          <Tab key={`tab-0`} label={t`My Positions`} />
          <Tab key={`tab-1`} label={t`History`} />
        </Tabs>
        <CurrencySelectWrapper>
          <CurrencyDropdown />
        </CurrencySelectWrapper>
      </StyledAppBar>
      <BodyScroller>
        <TabPanel key={'tab-panel-1'} activeIndex={activeTab} index={0}>
          {account ? (
            <AppBody size="lg" style={{ minHeight: '440px' }}>
              <Pools account={account} />
            </AppBody>
          ) : (
            <WalletConnect />
          )}
        </TabPanel>
        <TabPanel key={'tab-panel-2'} activeIndex={activeTab} index={1}>
          <AutoColumn gap="lg">
            <ResponsiveRow>{account ? <DashboardHistoryTab /> : <WalletConnect />}</ResponsiveRow>
          </AutoColumn>
        </TabPanel>
      </BodyScroller>
    </>
  )
}
